import {
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Layout,
  List,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import Timeline from 'react-calendar-timeline';
// make sure you include the timeline stylesheet or the timeline will not be styled
import { t } from '@hefe/i18n';
import { BasketballOperationsCenterGame } from '@hefe/models';
import { BasketballOperationsCenterApi } from '@hefe/store';
import { formatDatabaseDate } from '@hefe/utils';
import ConflictItemComponent from '../../components/conflict-item/conflict-item.component';
import OpenTaskItemComponent from '../../components/open-task-item/open-task-item.component';
import './timeline.css';

const { Header, Content } = Layout;
const { RangePicker } = DatePicker;

type Severity = 'critical' | 'warning';
type Status = 'green' | 'yellow' | 'red';

type TimelineItem = {
  id: number;
  group: number;
  title: string;
  start_time: Dayjs;
  end_time: Dayjs;
  color?: string;
};

type TimelineGroup = {
  id: number;
  title: string;
};

type Issue = {
  id: string;
  type: 'missing_task' | 'conflict';
  severity: Severity;
  title: string;
  subtitle?: string;
  startsAt: string; // ISO
};

type Helper = {
  id: string;
  name: string;
  load: number; // number of assignments
  conflicts: number;
};

function getNextWeekendRange(): [Dayjs, Dayjs] {
  // Next Saturday 00:00 -> Sunday 23:59
  const now = dayjs();
  const nextSat = now.day() <= 6 ? now.day(6) : now.add(1, 'week').day(6);
  const nextSun = nextSat.add(1, 'day');
  return [nextSat.startOf('day'), nextSun.endOf('day')];
}

const statusTag = (status: Status) => {
  if (status === 'green') return <Tag color="success">OK</Tag>;
  if (status === 'yellow') return <Tag color="warning">Fast</Tag>;
  return <Tag color="error">Kritisch</Tag>;
};

const severityBadge = (severity: Severity) => {
  if (severity === 'critical') return <Badge status="error" text="Kritisch" />;
  return <Badge status="warning" text="Warnung" />;
};

export default function OperationsCenterPage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(getNextWeekendRange());
  const [preset, setPreset] = useState<
    'Nächstes Wochenende' | 'Diese Woche' | 'Nächste Woche' | 'Custom'
  >('Nächstes Wochenende');

  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [search, setSearch] = useState('');

  const [loadData, { data, isFetching }] =
    BasketballOperationsCenterApi.useLazyFetchDataQuery();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    loadData({
      from: formatDatabaseDate(range[0].toDate()),
      to: formatDatabaseDate(range[1].toDate()),
    });
  };

  const games = useMemo<BasketballOperationsCenterGame[]>(() => {
    // games_by_id to flat array
    if (!data?.games_by_id) return [];

    return Object.values(data.games_by_id);
  }, [data]);

  const timelineItems: TimelineItem[] = useMemo(() => {
    return games.map((g) => ({
      id: Number(g.id),
      group: Number(g.gym_id ?? 0),
      title: `${g.home} - ${g.guest}`,
      start_time: dayjs(g.date),
      end_time: dayjs(g.date).add(2, 'hour'),
      color: 'red',
    }));
  }, [games]);

  const timelineGroups: TimelineGroup[] = useMemo(() => {
    // Extract unique gyms from games
    const gymMap: Record<number, string> = {};
    games.forEach((g) => {
      if (g.gym && g.gym_id) {
        gymMap[Number(g.gym_id)] = g.gym;
      }
    });

    // add away games gym_id null
    gymMap[0] = t('away_game');

    return Object.entries(gymMap).map(([id, title]) => ({
      id: Number(id),
      title,
    }));
  }, [games]);

  const filteredGames = useMemo(() => {
    return timelineItems.filter((g) => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        g.title.toLowerCase().includes(s) ||
        g.group.toString().toLowerCase().includes(s)
      );
    });
  }, [games, statusFilter, search]);

  const applyPreset = (p: typeof preset) => {
    setPreset(p);

    if (p === 'Nächstes Wochenende') {
      setRange(getNextWeekendRange());
      return;
    }

    if (p === 'Diese Woche') {
      setRange([dayjs().startOf('week'), dayjs().endOf('week')]);
      return;
    }

    if (p === 'Nächste Woche') {
      const start = dayjs().add(1, 'week').startOf('week');
      const end = dayjs().add(1, 'week').endOf('week');
      setRange([start, end]);
      return;
    }

    // Custom bleibt wie gewählt
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <Row align="middle" justify="space-between" gutter={[12, 12]}>
          <Col>
            <Space direction="vertical" size={0}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Spielbetrieb-Zentrale
              </Typography.Title>
              <Typography.Text type="secondary">
                Spiele, Aufgaben & Konflikte auf einen Blick
              </Typography.Text>
            </Space>
          </Col>

          <Col>
            <Space wrap>
              <Segmented
                value={preset}
                options={[
                  'Nächstes Wochenende',
                  'Diese Woche',
                  'Nächste Woche',
                  'Custom',
                ]}
                onChange={(v) => applyPreset(v as any)}
              />

              <RangePicker
                value={range}
                onChange={(v) => {
                  if (!v) return;
                  setRange(v as [Dayjs, Dayjs]);
                  setPreset('Custom');
                }}
                allowClear={false}
              />

              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                Aktualisieren
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>

      <Content style={{ padding: 16 }}>
        {/* KPI Row */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="Kritisch"
                value={data?.summary?.critical_count || 0}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="Fast fertig"
                value={data?.summary?.pending_count || 0}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title="Fertig"
                value={data?.summary?.resolved_count || 0}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          {/* LEFT: Timeline */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <Space>
                  <CalendarOutlined />
                  Timeline
                </Space>
              }
              extra={
                <Space>
                  <Input
                    allowClear
                    placeholder="Suchen (Team, Halle...)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 220 }}
                  />

                  <Select
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v)}
                    style={{ width: 150 }}
                    options={[
                      { label: 'Alle Status', value: 'all' },
                      { label: 'OK', value: 'green' },
                      { label: 'Fast', value: 'yellow' },
                      { label: 'Kritisch', value: 'red' },
                    ]}
                    suffixIcon={<FilterOutlined />}
                  />
                </Space>
              }
              style={{ height: '100%' }}
            >
              {/* Timeline Placeholder */}
              <div
                style={{
                  border: '1px dashed #d9d9d9',
                  borderRadius: 12,
                  padding: 16,
                  minHeight: 420,
                  background: '#fafafa',
                }}
              >
                <Timeline
                  groups={timelineGroups}
                  items={timelineItems}
                  defaultTimeStart={range[0].valueOf()}
                  defaultTimeEnd={range[1].valueOf()}
                  //visibleTimeStart={range[0].valueOf()}
                  //visibleTimeEnd={range[1].valueOf()}
                />
              </div>
            </Card>
          </Col>

          {/* RIGHT: Issues + Helpers */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Card
                title={
                  <Space>
                    <WarningOutlined />
                    Offene Aufgaben
                  </Space>
                }
                extra={<Tag color="red">{data?.open_tasks?.length || 0}</Tag>}
              >
                <List
                  dataSource={data?.open_tasks || []}
                  locale={{
                    emptyText: 'Alles zugeteilt. Ungewöhnlich, aber schön.',
                  }}
                  renderItem={(i, index) => (
                    <OpenTaskItemComponent
                      key={index}
                      item={i}
                      games={data?.games_by_id || {}}
                    />
                  )}
                />
              </Card>

              <Card
                title={
                  <Space>
                    <ExclamationCircleOutlined />
                    Konflikte
                  </Space>
                }
                extra={<Tag color="orange">{data?.conflicts?.length || 0}</Tag>}
              >
                <List
                  dataSource={data?.conflicts || []}
                  locale={{ emptyText: 'Keine Konflikte. Noch nicht.' }}
                  renderItem={(i, index) => (
                    <ConflictItemComponent
                      key={index}
                      conflict={i}
                      games={data?.games_by_id || {}}
                    />
                  )}
                />
              </Card>

              <Card
                title={
                  <Space>
                    <TeamOutlined />
                    Helfer-Übersicht
                  </Space>
                }
              >
                <List
                  dataSource={[]}
                  renderItem={(h: any) => (
                    <List.Item>
                      <Space
                        direction="vertical"
                        size={0}
                        style={{ width: '100%' }}
                      >
                        <Space
                          style={{
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <Typography.Text strong>{h.name}</Typography.Text>
                          <Space>
                            <Tag>{h.load} Einsätze</Tag>
                            {h.conflicts > 0 ? (
                              <Tag color="orange">{h.conflicts} Konflikt</Tag>
                            ) : (
                              <Tag color="green">OK</Tag>
                            )}
                          </Space>
                        </Space>
                        <Typography.Text type="secondary">
                          Tip: Click später auf Person → Detailansicht /
                          Timeline
                        </Typography.Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            </Space>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}
